// Reading a settings version (the playbook) as plain sections, and comparing a draft with the active version.
import type { Json, JsonObject } from "./types";
import { humanise } from "./format";

export interface SectionDef {
  id: string;
  title: string;
  blurb: string;
  keys: string[];
}

/** How the playbook is presented at 0.5. Keys not listed here appear under "Everything else". */
export const SECTIONS: SectionDef[] = [
  { id: "customer", title: "Ideal customer", blurb: "Who you sell to, where they are and how we find them.", keys: ["ideal_customer", "markets", "target"] },
  { id: "offer", title: "Offer", blurb: "What you offer, the free resource, and the one thing we ask people to do.", keys: ["offer"] },
  { id: "voice", title: "Voice and brand", blurb: "How messages and posts should sound and look.", keys: ["voice", "brand", "newsletter"] },
  { id: "channels", title: "Channels and countries", blurb: "Which channels are switched on, the rules per country, sequences and sending limits.", keys: ["channels", "compliance", "sequences", "deliverability", "social"] },
  { id: "tools", title: "Tools", blurb: "Which service does each job. This drives the accounts you are asked to connect.", keys: ["tools", "lead_sources", "enrichment"] },
  { id: "gates", title: "Approval gates", blurb: "The decisions that always come to you before anything happens.", keys: ["human_gates", "automation"] },
];

const SECTION_KEYS = new Set(SECTIONS.flatMap((s) => s.keys));

/** Human-readable names for the gate step ids. */
export const GATE_LABELS: Record<string, string> = {
  "0.5": "Approve the playbook",
  "1.21": "Approve each social post",
  "4.10": "Approve each batch of outreach messages",
  "6.11": "Decide replies the inbox agent is unsure about",
  "8.3": "Confirm meeting notes and next steps",
  "9.6": "Approve offers and contracts before they are sent",
};

export function sectionsFor(config: JsonObject): { def: SectionDef; value: JsonObject }[] {
  const out = SECTIONS.map((def) => {
    const value: JsonObject = {};
    for (const k of def.keys) if (config[k] !== undefined) value[k] = config[k]!;
    return { def, value };
  });
  const rest: JsonObject = {};
  for (const [k, v] of Object.entries(config)) if (!SECTION_KEYS.has(k)) rest[k] = v;
  if (Object.keys(rest).length) out.push({ def: { id: "other", title: "Everything else", blurb: "Other settings.", keys: Object.keys(rest) }, value: rest });
  return out;
}

export type Change = "added" | "removed" | "changed" | "same";

export interface DiffRow {
  path: string;
  label: string;
  before: string | null;
  after: string | null;
  change: Change;
  /** The value is a list of plain values (shown comma-separated). */
  list?: boolean;
}

function isPlainObject(v: unknown): v is JsonObject {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function scalar(v: Json): string {
  if (v === null) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string") return v === "" ? "—" : v;
  return String(v);
}

/** Flattens settings to readable rows. Lists of plain values become one comma-separated row; lists of objects are numbered. */
export function flatten(value: Json | undefined, prefix = ""): Map<string, string> {
  const out = new Map<string, string>();
  const walk = (v: Json, path: string) => {
    if (isPlainObject(v)) {
      const entries = Object.entries(v);
      if (!entries.length && path) out.set(path, "—");
      for (const [k, child] of entries) walk(child, path ? `${path}.${k}` : k);
    } else if (Array.isArray(v)) {
      if (v.every((x) => !isPlainObject(x) && !Array.isArray(x))) out.set(path, v.length ? v.map((x) => scalar(x)).join(", ") : "—");
      else v.forEach((x, i) => walk(x, `${path}[${i + 1}]`));
    } else {
      out.set(path, scalar(v));
    }
  };
  if (value !== undefined) walk(value, prefix);
  return out;
}

/** Plainer names for setting keys a business owner meets. */
export const KEY_LABELS: Record<string, string> = {
  daily_cap_per_inbox: "Emails per mailbox per day",
  lead_magnet: "Free resource",
  ask: "What we ask for",
  human_gates: "Approval gates",
  expected_hours: "Hours allowed between runs",
  owner_intro: "Owner introduction",
};

/** "channels.email.daily_cap_per_inbox" -> "Channels › Email › Emails per mailbox per day". */
export function pathLabel(path: string): string {
  return path
    .split(".")
    .map((part) => {
      const m = /^(.*?)\[(\d+)\]$/.exec(part);
      const key = m ? m[1]! : part;
      const label = GATE_LABELS[key] ? key : (KEY_LABELS[key] ?? humanise(key));
      return m ? `${label} ${m[2]}` : label;
    })
    .join(" › ");
}

/** Drops the first part of a label when the section heading already says it ("Offer › Ask" under "Offer"). */
export function labelInSection(label: string, sectionTitle: string): string {
  const parts = label.split(" › ");
  if (parts.length > 1 && sectionTitle.toLowerCase().startsWith(parts[0]!.toLowerCase())) return parts.slice(1).join(" › ");
  return label;
}

/** For a list shown as "a, b, c": which items were added and removed. Null when either side is not a list. */
export function listChange(before: string | null, after: string | null): { kept: string[]; added: string[]; removed: string[] } | null {
  if (before === null || after === null || (!before.includes(", ") && !after.includes(", "))) return null;
  const a = before.split(", ");
  const b = after.split(", ");
  return { kept: b.filter((x) => a.includes(x)), added: b.filter((x) => !a.includes(x)), removed: a.filter((x) => !b.includes(x)) };
}

/** Paths whose value is a list of plain values, as flatten() names them. */
export function listPaths(value: Json | undefined, prefix = ""): Set<string> {
  const out = new Set<string>();
  const walk = (v: Json, path: string) => {
    if (isPlainObject(v)) for (const [k, child] of Object.entries(v)) walk(child, path ? `${path}.${k}` : k);
    else if (Array.isArray(v)) {
      if (v.every((x) => !isPlainObject(x) && !Array.isArray(x))) out.add(path);
      else v.forEach((x, i) => walk(x, `${path}[${i + 1}]`));
    }
  };
  if (value !== undefined) walk(value, prefix);
  return out;
}

export function diffConfig(before: JsonObject | null, after: JsonObject, keys?: string[]): DiffRow[] {
  const pick = (c: JsonObject | null): JsonObject => {
    if (!c) return {};
    if (!keys) return c;
    const o: JsonObject = {};
    for (const k of keys) if (c[k] !== undefined) o[k] = c[k]!;
    return o;
  };
  const a = flatten(pick(before));
  const b = flatten(pick(after));
  const lists = new Set([...listPaths(pick(before)), ...listPaths(pick(after))]);
  const paths = [...new Set([...b.keys(), ...a.keys()])];
  return paths.map((path) => {
    const was = a.has(path) ? a.get(path)! : null;
    const now = b.has(path) ? b.get(path)! : null;
    const change: Change = was === null ? "added" : now === null ? "removed" : was === now ? "same" : "changed";
    return { path, label: pathLabel(path), before: was, after: now, change, ...(lists.has(path) ? { list: true } : {}) };
  });
}

export function countChanges(rows: DiffRow[]): number {
  return rows.filter((r) => r.change !== "same").length;
}
